import produce from 'immer';
import create, { StateCreator } from 'zustand';
import companyApi from 'lib/api/company';
// eslint-disable-next-line import/no-cycle
import chartbitApi from 'lib/api/chartbit';
import { IItemOrderbookListItem } from 'features/orderbook/types';
import { CompanyInfo } from '../../../@types/company';
import { ChartbitLabelType, UserSettingType } from './types';

type LabelMenuType = {
  visible: boolean;
  data: ChartbitLabelType | null;
};

export type ChartbitState = {
  changes: {
    haveChanges: boolean;
    modalCallback: null | ((value: boolean) => void);
    layoutIdToLoad: null | string | number;
  };
  currentLayoutId: any;
  myInvestment: {
    visible: boolean;
  };
  createdLayoutId: any;
  activeModalUnsaved: string;
  widgetKey: string;
  isOpenNewLayout: boolean;
  showOverlay: boolean;
  userSettings: UserSettingType;
  currentContent: any;
  lastActiveWidgetKey: string;
  symbolMaps: { [key: number]: string };
  labelMenu: LabelMenuType;
  companyDetail: Record<string, { data: CompanyInfo; time: number }>;
  quoteData: Record<
    string,
    { data?: IItemOrderbookListItem | null; time: number }
  >;
};

export type ChartbitActions = {
  closeUnsavedModal: () => void;
  openUnsavedModal: (widgetKey: string) => Promise<boolean>;
  setHaveChanges: (value: boolean) => void;
  setVisibleMyInvestment: (value: boolean) => void;
  setCurrentLayoutId: (value: any) => void;
  setCreatedLayoutId: (value: any) => void;
  setWidgetKey: (value: string) => void;
  setIsOpenNewLayout: (value: boolean) => void;
  setShowOverlay: (value: boolean) => void;
  setUserSetting: ({
    key,
    value,
  }: {
    key: string;
    value: string;
  }) => Promise<UserSettingType>;
  setBulkUserSetting: (value: UserSettingType) => void;
  setCurrentContent: (value: any) => void;
  setLastActiveWidgetKey: (value: string) => void;
  setSymbolMaps: (index: number, symbol: string) => void;
  createSymbolMaps: (item: Record<number, string>) => void;
  setLabelMenu: (visible: boolean, data?: ChartbitLabelType) => void;
  fetchCompanyDetail: (symbol: string) => void;
  fetchQuoteData: (symbol: string) => Promise<IItemOrderbookListItem>;
};

export type ChartbitStore = ChartbitState & ChartbitActions;

const chartbitState: ChartbitState = {
  changes: {
    haveChanges: false,
    modalCallback: null,
    layoutIdToLoad: null,
  },
  currentLayoutId: null,
  myInvestment: {
    visible: false,
  },
  createdLayoutId: null,
  activeModalUnsaved: null,
  widgetKey: null,
  isOpenNewLayout: false,
  showOverlay: true,
  userSettings: {},
  currentContent: {},
  lastActiveWidgetKey: null,
  symbolMaps: {},
  labelMenu: {
    visible: false,
    data: null,
  },
  companyDetail: {},
  quoteData: {},
};

const chartbitActions: StateCreator<ChartbitStore, [], [], ChartbitActions> = (
  set,
  get,
) => ({
  closeUnsavedModal: () => {
    set((state) =>
      produce(state, (draft) => {
        draft.changes.modalCallback = null;
        draft.activeModalUnsaved = null;
      }),
    );
  },
  openUnsavedModal: (widgetKey: string) =>
    new Promise((resolve) => {
      const cb = (value: boolean) => {
        resolve(value);
        get().closeUnsavedModal();
      };

      set((state) =>
        produce(state, (draft) => {
          draft.activeModalUnsaved = widgetKey;
          draft.changes.modalCallback = cb;
        }),
      );
    }),
  setHaveChanges: (value) => {
    set((state) =>
      produce(state, (draft) => {
        draft.changes.haveChanges = value;
      }),
    );
  },
  setVisibleMyInvestment: (value: boolean) => {
    set({ myInvestment: { visible: value } });
  },
  setCurrentLayoutId: (value: any) => {
    set({ currentLayoutId: value });
  },
  setCreatedLayoutId: (value: any) => {
    set({ createdLayoutId: value });
  },
  setWidgetKey: (value: any) => {
    set({ widgetKey: value });
  },
  setIsOpenNewLayout: (value: boolean) => {
    set({ isOpenNewLayout: value });
  },
  setShowOverlay: (value: boolean) => {
    set({ showOverlay: value });
  },
  setUserSetting: ({ key, value }: { key: string; value: string }) =>
    new Promise((res) => {
      const current = get().userSettings;
      const newSetting = { ...current, [key]: value };
      set({ userSettings: newSetting });
      res(newSetting);
    }),
  setBulkUserSetting: (setting: UserSettingType) => {
    set({ userSettings: setting });
  },
  setCurrentContent: (value: any) => {
    set({ currentContent: value });
  },
  setLastActiveWidgetKey: (value: string) => {
    set({ lastActiveWidgetKey: value });
  },
  setSymbolMaps: (index, symbol) => {
    set({ symbolMaps: { ...get().symbolMaps, [index]: symbol } });
  },
  createSymbolMaps: (item) => {
    set({ symbolMaps: { ...get().symbolMaps, ...item } });
  },
  setLabelMenu: (visible, data) => {
    set({ labelMenu: { visible, data } });
  },
  fetchCompanyDetail: async (symbol: string) => {
    try {
      if (
        get().companyDetail[symbol] &&
        Date.now() - get().companyDetail[symbol].time < 1 * 60 * 60
      ) {
        return;
      }
      const res = await companyApi.getCompanyInfo(symbol);

      const companyData = res?.data?.data;

      if (companyData) {
        set({
          companyDetail: {
            ...get().companyDetail,
            [symbol]: { time: Date.now(), data: companyData },
          },
        });
      }
    } catch (err) {
      // no action
    }
  },
  fetchQuoteData: async (symbol: string) => {
    const currTime = new Date().getTime();
    const savedQuote = get().quoteData[symbol];

    if (savedQuote && currTime - savedQuote?.time < 1000) {
      if (savedQuote?.data) return savedQuote.data;
      return null;
    }

    set({
      quoteData: {
        ...get().quoteData,
        [symbol]: {
          data: null,
          time: currTime,
        },
      },
    });

    const response = await chartbitApi.getQuote(symbol);

    if (!response.data) {
      throw new Error('Attempt to get quote failed');
    }

    const { data, error } = response.data;
    if (error) throw new Error('Attempt to get quote failed');

    set({
      quoteData: {
        ...get().quoteData,
        [symbol]: {
          data: response.data.data,
          time: currTime,
        },
      },
    });
    return data;
  },
});

export const useChartbitStore = create<ChartbitStore>(
  (set, get, store, storeMutation) => ({
    ...chartbitState,
    ...chartbitActions(set, get, store, storeMutation),
  }),
);
